import { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { ENDPOINTS } from 'config'

import { type OutagesForm } from 'containers/Outages'
import {
  type AccountResponse,
  type BillingResponse,
  type ICAuthResponse,
  type PaymentResponse,
  type UsageResponse
} from 'utils/hooks/types'
import {
  type IAdmin,
  type IBilling,
  type ICloudPay,
  type IConfirmRegister,
  type IContact,
  type IICAuth,
  type IInvoice,
  type IPayments,
  type IRegister,
  type IService,
  type IUpdateMailAddress,
  type IUpdateUser,
  type IUsageV2,
  type IUser,
  type IVerifyPayBill
} from './apiTypes'
import { axios } from './axiosInstance'

interface IVerifyAccountParam {
  email: string
  lastFourId: string
  accountId: string
  AccessToken?: string
}

interface ICheckEmailParam {
  email: string
}

interface ICheckEmailResponse {
  emailExists: boolean
}

interface IVerifyAccountResponse {
  status: number
  verificationToken?: string
  message?: { message?: string }
}

interface CloudPayResponse {
  data?: {
    Data?: {
      CloudPaymentURL: string
    }
  }
  error?: {
    Message: string
    ModelState: Record<string, unknown>
  }
}

export interface MaintenanceModeResponse {
  isSystemUnderMaintenance: boolean
}

export interface UpdateMailAddressResponse {
  status: string
  message?: string
}

class RestAPIs {
  private readonly requestInstance: AxiosInstance

  constructor(axiosInstance: AxiosInstance) {
    this.requestInstance = axiosInstance
  }

  private getHeader(token: string): AxiosRequestConfig {
    return {
      headers: { Authorization: `Bearer ${token}` }
    }
  }

  getCloudData = async (param: ICloudPay): Promise<CloudPayResponse> =>
    await this.requestInstance.post(ENDPOINTS.CLOUDPAY, param)

  updateUser = async (param: IUpdateUser): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(
      param.admin === true ? ENDPOINTS.UPDATE_AUTH_ADMIN : ENDPOINTS.UPDATE,
      param,
      this.getHeader(param.AccessToken)
    )

  registerUser = async (param: IRegister): Promise<{ status?: number, message?: { message?: string } }> =>
    await this.requestInstance.post(ENDPOINTS.REGISTER, param)

  verifyAccount = async (param: IVerifyAccountParam): Promise<IVerifyAccountResponse> =>
    await this.requestInstance.post(
      ENDPOINTS.VERIFY_ACCOUNT,
      param,
      param.AccessToken != null ? this.getHeader(param.AccessToken) : undefined
    )

  checkEmail = async (param: ICheckEmailParam): Promise<ICheckEmailResponse> =>
    await this.requestInstance.post(
      ENDPOINTS.CHECK_EMAIL,
      param
    )

  contactUs = async (param: IContact): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(
      ENDPOINTS.CONTACT_US,
      param,
      this.getHeader(param.AccessToken)
    )

  getAdmin = async (param: IAdmin): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(ENDPOINTS.ADMIN, param)

  getAccount = async (param: IUser): Promise<AccountResponse> =>
    await this.requestInstance.post(
      param.admin === true ? ENDPOINTS.ACCOUNT_AUTH_ADMIN : ENDPOINTS.ACCOUNT,
      param,
      this.getHeader(param.AccessToken)
    )

  getICAuth = async (param: IICAuth): Promise<ICAuthResponse> =>
    await this.requestInstance.post(
      param.admin === true ? ENDPOINTS.IC_AUTH_ADMIN : ENDPOINTS.IC_AUTH,
      param,
      this.getHeader(param.AccessToken)
    )

  getBilling = async (param: IBilling): Promise<BillingResponse> =>
    await this.requestInstance.post(
      param.admin === true ? ENDPOINTS.BILLING_AUTH_ADMIN : ENDPOINTS.BILLING,
      param,
      this.getHeader(param.AccessToken)
    )

  getPayments = async (param: IPayments): Promise<PaymentResponse> =>
    await this.requestInstance.post(
      param.admin === true ? ENDPOINTS.PAYMENT_AUTH_ADMIN : ENDPOINTS.PAYMENT,
      param,
      this.getHeader(param.AccessToken)
    )

  getUsage = async (param: IUsageV2): Promise<UsageResponse> =>
    await this.requestInstance.post(
      param.admin === true ? ENDPOINTS.USAGE_V2_AUTH_ADMIN : ENDPOINTS.USAGE_V2,
      param,
      this.getHeader(param.AccessToken)
    )

  updateMailAddress = async (param: IUpdateMailAddress): Promise<UpdateMailAddressResponse> =>
    await this.requestInstance.put(
      param.admin === true
        ? ENDPOINTS.UPDATE_MAIL_ADDRESS_AUTH_ADMIN
        : ENDPOINTS.UPDATE_MAIL_ADDRESS,
      param,
      this.getHeader(param.AccessToken)
    )

  sendServiceRequest = async (param: IService): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(
      ENDPOINTS.SERVICE_REQUEST,
      param,
      this.getHeader(param.AccessToken)
    )

  getOutages = async (): Promise<Record<string, unknown>> =>
    await this.requestInstance.get(ENDPOINTS.OUTAGES)

  verifyPayBill = async (param: IVerifyPayBill): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(ENDPOINTS.VERIFY_PAY_BILL, param)

  getInvoiceAccount = async (param: IInvoice): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(
      ENDPOINTS.RETRIEVE_IC_ACCOUNT,
      param,
      this.getHeader(param.AccessToken)
    )

  confirmRegister = async (param: IConfirmRegister): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(ENDPOINTS.CONFIRM_REGISTER, param)


  reportOutage = async (param: OutagesForm): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(ENDPOINTS.REPORT_OUTAGE, param)

  getMaintenanceMode = async (): Promise<MaintenanceModeResponse> =>
    await this.requestInstance.get(ENDPOINTS.MAINTENANCE_MODE)

  requestEmailUpdate = async (param: { newEmail: string, AccessToken: string }): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(ENDPOINTS.REQUEST_EMAIL_UPDATE, param, this.getHeader(param.AccessToken))

  confirmEmailUpdate = async (param: { code: string, AccessToken: string }): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(ENDPOINTS.CONFIRM_EMAIL_UPDATE, param, this.getHeader(param.AccessToken))

  requestPasswordChange = async (param: { AccessToken: string }): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(ENDPOINTS.REQUEST_PASSWORD_CHANGE, param, this.getHeader(param.AccessToken))

  changePassword = async (param: { code: string, currentPassword: string, newPassword: string, AccessToken: string }): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(ENDPOINTS.CHANGE_PASSWORD, param, this.getHeader(param.AccessToken))

  passwordResetConfirm = async (param: { email: string, AccessToken: string }): Promise<Record<string, unknown>> =>
    await this.requestInstance.post(ENDPOINTS.PASSWORD_RESET_CONFIRM, param, this.getHeader(param.AccessToken))
}

export const APIs = new RestAPIs(axios)
